<template>
    <div>
        <div class="bg-blue-100 text-blue-800 p-4 rounded" v-if="unassignedCartItems.length > 0 && !isOffline">
            <p class="text-lg">You have {{ unassignedCartItems.length }} unassigned extra cards.</p>
            <p class="text-lg">Please assign them to participants.</p>
            <div class="flex justify-center">
                <AppButton
                    title="Assign Extra Card"
                    button-type="Secondary"
                    class="mt-2"
                    @click.stop="assign"
                />
            </div>
        </div>
        <div :class="{ 'flex': showModal, 'hidden': !showModal }" class="fixed inset-0 bg-black bg-opacity-50 z-50 items-center justify-center">
            <div class="p-4 bg-white text-primary rounded-lg max-w-md w-full mx-4" v-if="showModal">
                <Notification class="text-lg my-2" :message="message" :message-type="messageType"
                    :details="messageDetails" />

                <AssignExtraCards :event-participants="availableParticipants" :quantity="unassignedCartItems.length"
                    @assignment-updated="extraCardAssignmentUpdated"></AssignExtraCards>
                <p class="text-xl my-2">{{ extraCardAssignment.length }} of {{ unassignedCartItems.length }} extra
                    cards assigned.
                </p>
                <div class="flex justify-center">
                    <AppButton
                        title="Assign"
                        :disabled="extraCardAssignment.length < unassignedCartItems.length"
                        class="my-2"
                        @click.stop="save"
                    />
                </div>
                <div class="flex justify-center">
                    <AppCancel class="pb-10" @click="showModal = false" />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useEventParticipantStore } from "@/store/EventParticipant";
import { useUserProfileStore } from "@/store/UserProfile";
import AssignExtraCards from "@/components/AssignExtraCards.vue";
import { storeToRefs } from "pinia";
import { computed, ref } from "vue";
import { EventParticipant } from "@models/EventParticipant";
import Notification from "@/components/Notification.vue";
import AppButton from "@/components/AppButton.vue";
import AppCancel from "@/components/AppCancel.vue";

const { profile } = useUserProfileStore();
const {
    assignParticipantsToExtraCards
} = useEventParticipantStore();

const {
    currentPokerRunEvent,
    cartItems,
    isOffline,
    currentEventParticipants
} = storeToRefs(useEventParticipantStore());

const showModal = ref(false);
const message = ref("");
const messageDetails = ref("");
const messageType = ref<"success" | "warning" | "danger" | "info">("info");
const extraCardAssignment = ref<string[]>([]);
const unassignedCartItems = computed(() => cartItems.value.filter(
    (item) => item.participantId === null || item.participantId === undefined));

const availableParticipants = computed(() => {
    const participants = currentEventParticipants.value.filter((participant) => !participant.extraCard)
        .filter((participant) => !cartItems.value.some((item) => item.participantId === participant.id))
        .map((participant) => {
            const index = currentEventParticipants.value.indexOf(participant);
            return {
                id: participant.id,
                name: participant.name ?? "Hand " + (index + 1)
            } as unknown as EventParticipant;
        });
    return participants
}
);

const extraCardAssignmentUpdated = (assignments: string[]) => {
    extraCardAssignment.value = assignments;
};

const assign = () => {
    extraCardAssignment.value = [];
    showModal.value = true
};

const save = async () => {
    try {
        await assignParticipantsToExtraCards(currentPokerRunEvent.value.id, profile.id, extraCardAssignment.value);
        showModal.value = false;
    } catch (error) {
        message.value = "An error occurred while assigning extra cards. Please try again.";
        messageType.value = "danger";
        messageDetails.value = error.message;
    }
};
</script>
